// ex: getRouteWithVariablesReplaced("search/advanced(/:string)(/page/:page)", "test", 1);
// output: /search/advanced/test/page/1
// ex: getRouteWithVariablesReplaced("search/advanced(/:string)(/page/:page)", "test");
// output: /search/advanced/test
// ex: getRouteWithVariablesReplaced("search/within/:string(/page/:page)", "test", 1);
// output: /search/within/test/page/1
// ex: getRouteWithVariablesReplaced("search/within/:string(/page/:page)", "test");
// output: /search/within/test
// ex: getRouteWithVariablesReplaced("search/basic/*string(/page/:page)", "test", 1);
// output: /search/basic/test/page/1
// ex: getRouteWithVariablesReplaced("search/basic/*string(/page/:page)", "test");
// output: /search/basic/test
// ex: getRouteWithVariablesReplaced("search/basic/*string(/page/:page)", undefined);
// output: /search/basic

export type ExtractRouteParams<T extends string> =
	T extends `${infer _Start}/:${infer Param}(/:${infer OptionalParam})`
		? [Param: string, OptionalParam?: string]
		: T extends `${infer _Start}/:${infer Param}`
		? [Param: string]
		: [];

export type StringReplace<
	T extends string,
	S extends string,
	R extends string,
> = T extends `${infer Prefix}${S}${infer Suffix}`
	? `${Prefix}${R}${Suffix}`
	: T;

const isOptionalSegment = (segment: string = ''): boolean =>
	/^\(\/.*\)$/.test(segment);

const isRouteVariable = (segment: string = ''): boolean =>
	segment.startsWith(':') || segment.startsWith('*');

export const getRouteWithVariablesReplaced = <
	T extends string,
	R extends string,
>(
	route: T,
	...args: Array<number | string | undefined>
): R => {
	// keep track of the index we are at in the args array
	let argIndex = 0;
	// for building the resulting route
	let resultingRoute = '';

	const segments = route.match(/[^/()]+|\([^()]*\)/g) ?? [];
	// [ 'search', 'advanced', '(/:string)', '(/page/:page)' ]
	const totalSegments = segments.length;

	const parseOptionalSegment = (segment: string, index: number) => {
		const segmentWithoutParen = segment.slice(2, -1);
		const segementHasArgs =
			segmentWithoutParen.includes(':') || segmentWithoutParen.startsWith('*');
		const segmentParts = segmentWithoutParen.split('/');

		let foundArg = false;
		let resultingSegment = '';
		for (let i = 0, c = segmentParts.length; i < c; i++) {
			const subSegment = segmentParts[i];
			if (isRouteVariable(subSegment)) {
				const shouldTestPrev = argIndex > 0;
				const value = args[argIndex++];
				if (value != null) {
					// we have not found a previous argument to this ones
					if (shouldTestPrev && args[argIndex - 2] == null) {
						throw new Error(
							`Has value for ${subSegment} but missing value for previous variable segment ${
								segments[index - 1]
							}`,
						);
					}
					resultingSegment += `/${value}`;
					foundArg = true;
					continue;
				}
				// once we have an arg in within parenthesis, all following
				// segments must not be optional
				if (foundArg) {
					throw new Error(
						`Missing value for route variable in segment ${subSegment}`,
					);
				}
			} else {
				resultingSegment += `/${subSegment}`;
			}
		}

		if (foundArg || !segementHasArgs) {
			resultingRoute += resultingSegment;
		}
	};

	const parseRequiredSegment = (segment: string, index: number) => {
		if (isRouteVariable(segment)) {
			const shouldTestPrev = argIndex > 0;
			const value = args[argIndex++];
			if (value == null) {
				throw new Error(
					`Missing value for route variable in segment ${segment}`,
				);
			}
			// we have not found a previous argument to this ones
			if (shouldTestPrev && args[argIndex - 2] == null) {
				throw new Error(
					`Has value for ${segment} but missing value for previous variable segment ${
						segments[index - 1]
					}`,
				);
			}
			resultingRoute += `/${value}`;
		} else {
			resultingRoute += `/${segment}`;
		}
	};

	for (let i = 0; i < totalSegments; i++) {
		const segment = segments[i];
		isOptionalSegment(segment)
			? // handle optional segments
			  // ex: (/:string) or (/page/:page)
			  parseOptionalSegment(segment, i)
			: // handle required segments
			  parseRequiredSegment(segment, i);
	}

	return resultingRoute as R;
};
